import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';

// Helper function to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.matchMedia && window.matchMedia("(max-width: 768px)").matches);
};

// Helper function to download PDF with mobile compatibility
const downloadPdfWithMobileSupport = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);
  
  if (isMobileDevice()) {
    // For mobile devices, use window.open which is more reliable
    const newWindow = window.open(blobUrl, '_blank');
    if (!newWindow) {
      // If popup blocked, fallback to creating a link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Dispatch a click event instead of calling click()
      const clickEvent = new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true
      });
      link.dispatchEvent(clickEvent);
      
      // Clean up after a delay
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
      }, 100);
    } else {
      // Clean up blob URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
      }, 1000);
    }
  } else {
    // Desktop approach
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);
  }
};

export const savePdf = async (data, testata) => {
  try {
    const doc = new jsPDF('landscape', 'in', 'a4');

    const marginLeft = 0.5;
    const marginTop = 0.5;

    // Aggiunta del logo
    const logoWidth = 0.5;
    const logoHeight = 0.5;
    doc.addImage("/assets/cedi.png", "PNG", marginLeft, marginTop, logoWidth, logoHeight);

    // Informazioni della sede legale (opzionale, puoi rimuoverle se non desiderate)
    doc.setFontSize(10);
    const legalX = marginLeft + logoWidth + 0.2;
    let currentY = marginTop;
    doc.text("Sede Legale", legalX, currentY + 0.2);
    doc.text("Via Luca da Penne, 3", legalX, currentY + 0.35);
    doc.text("80122 - NAPOLI", legalX, currentY + 0.5);
    doc.text("C.F: 04805240639 - P.I. 04805240639", legalX, currentY + 0.65);
    doc.text("REA Napoli n. 396782", legalX, currentY + 0.8);

    // Box dei totali (stile grigio chiaro con bordi arrotondati come nel PDF degli ordini)
    const boxWidth = 3; // Larghezza uguale al PDF degli ordini
    const boxHeight = 1.2; // Altezza simile al PDF degli ordini
    const pageWidth = doc.internal.pageSize.getWidth();
    const boxX = pageWidth - marginLeft - boxWidth;
    const boxPadding = 0.15; // Padding come nel PDF degli ordini

    // Box di sfondo con bordi arrotondati (sfondo grigio chiaro #F8F8F8)
    doc.setFillColor(248, 248, 248); // Sfondo grigio chiaro (#F8F8F8)
    doc.roundedRect(boxX, marginTop, boxWidth, boxHeight, 0.1, 0.1, 'F'); // Bordi arrotondati di raggio 0.1

    // Bordo sottile (grigio chiaro come nel PDF degli ordini)
    doc.setDrawColor(221, 221, 221); // Grigio chiaro per il bordo
    doc.setLineWidth(0.01);
    doc.roundedRect(boxX, marginTop, boxWidth, boxHeight, 0.1, 0.1, 'S');

    // Formattazione dei totali dalla testata
    const quantitaOrdinata = testata?.quantita_ordinata?.toLocaleString() || '0';
    const importoOrdinato = Number(testata?.importo_ordinato || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    const quantitaAssegnata = testata?.quantita_assegnata?.toLocaleString() || '0';
    const importoAssegnato = Number(testata?.importo_assegnato || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });

    // Inserimento dei totali nel box (stile e struttura simili al PDF degli ordini)
    const lineSpacing = 0.2; // Spaziatura tra le righe come nel PDF degli ordini
    let textY = marginTop + boxPadding + 0.1;
    const valueX = boxX + boxWidth - boxPadding; // Allineamento a destra per i valori
    const labelX = boxX + boxPadding; // Allineamento a sinistra per le etichette

    // Totale quantità ordinata residua
    doc.setFontSize(8); // Dimensione del font come nel PDF degli ordini
    doc.text("Totale quantità ordinata residua:", labelX, textY);
    doc.text(quantitaOrdinata, valueX, textY, { align: 'right' });

    // Valore ordinato residuo
    textY += lineSpacing;
    doc.text("Valore ordinato residuo:", labelX, textY);
    doc.text(importoOrdinato, valueX, textY, { align: 'right' });

    // Linea separatrice (come nel PDF degli ordini)
    textY += lineSpacing;
    doc.setDrawColor(200, 200, 200); // Grigio medio per la linea
    doc.line(labelX, textY, valueX, textY);

    // Totale quantità assegnata (in grassetto per evidenziare, come il "TOTALE ORDINI" nel PDF degli ordini)
    textY += lineSpacing;
    doc.setFont(undefined, 'bold'); // Testo in grassetto
    doc.text("Totale quantità assegnata:", labelX, textY);
    doc.text(quantitaAssegnata, valueX, textY, { align: 'right' });

    // Valore assegnato (in grassetto per evidenziare, come il "TOTALE ORDINI" nel PDF degli ordini)
    textY += lineSpacing;
    doc.text("Valore assegnato:", labelX, textY);
    doc.setFont(undefined, 'normal'); // Torna al normale
    doc.text(importoAssegnato, valueX, textY, { align: 'right' });

    // Titolo della lista backorder
    currentY += 1.2;
    const title = `BACKORDER ${moment().format('DD/MM/YYYY')}`;
    doc.setFontSize(12);
    doc.text(title, marginLeft, currentY);

    // Preparazione dei dati della tabella (rimane invariata)
    const tableHead = [
      'RAGIONE SOCIALE',
      'RIF. CLIENTE',
      'N° ORDINE',
      'DATA ORDINE',
      'DATA CONSEGNA RICHIESTA',
      'CODICE ARTICOLO',
      'DESCRIZIONE ARTICOLO',
      'QTÀ',
      'IMPORTO',
      'QTÀ ASS.',
      'IMPORTO ASS.',
      'DEPOSITO',
      'RITIRO CLIENTE'
    ];

    const tableRows: any[] = [];
    const subtotalRowIndices: number[] = [];

    let currentKey = '';
    let sumQta = 0;
    let sumImporto = 0;
    let sumQtaAss = 0;
    let sumImportoAss = 0;

    const pushSubtotal = () => {
      subtotalRowIndices.push(tableRows.length);
      tableRows.push([
        '', '', '', '', '',
        '',
        'TOTALE ORDINE',
        sumQta,
        Number(sumImporto).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
        sumQtaAss,
        Number(sumImportoAss).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
        '', ''
      ]);
      sumQta = 0;
      sumImporto = 0;
      sumQtaAss = 0;
      sumImportoAss = 0;
    };

    data.forEach((row) => {
      const key = `${row.RIFERIMENTO_CLIENTE}|${row.NUMERO_ORDINE}|${row.DATA_ORDINE}`;

      if (currentKey !== '' && key !== currentKey) {
        pushSubtotal();
      }
      currentKey = key;

      sumQta += Number(row.QUANTITA || 0);
      sumImporto += Number(row.PREZZO || 0);
      sumQtaAss += Number(row.QUANTITA_ASSEGNATA || 0);
      sumImportoAss += Number(row.IMPORTO || 0);

      tableRows.push([
        row.RAGIONE_SOCIALE || '',
        row.RIFERIMENTO_CLIENTE || '',
        row.NUMERO_ORDINE || '',
        row.DATA_ORDINE ? moment(row.DATA_ORDINE.toString()).format('DD/MM/YYYY') : '',
        row.DATA_CONSEGNA_RICHIESTA ? moment(row.DATA_CONSEGNA_RICHIESTA.toString()).format('DD/MM/YYYY') : '',
        row.CODICE_ARTICOLO || '',
        row.DESCRIZIONE_ARTICOLO || '',
        row.QUANTITA || '0',
        Number(row.PREZZO || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
        row.QUANTITA_ASSEGNATA || '0',
        Number(row.IMPORTO || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }),
        row.DEPOSITO || '',
        row.RITIRO_CLIENTE || ''
      ]);
    });

    if (data.length > 0) {
      pushSubtotal();
    }

    const tableStartY = currentY + 0.3;
    let pageNumber = 1;

    autoTable(doc, {
      startY: tableStartY,
      head: [tableHead],
      body: tableRows,
      styles: {
        fontSize: 8,
        cellPadding: 0.1,
        lineColor: [221, 221, 221],
        lineWidth: 0.01
      },
      headStyles: {
        fillColor: [248, 248, 248],
        textColor: [0, 0, 0],
        fontStyle: 'bold'
      },
      margin: { left: marginLeft, right: marginLeft },
      didParseCell: (data) => {
        if (data.section === 'body' && subtotalRowIndices.includes(data.row.index)) {
          data.cell.styles.fillColor = [220, 220, 220];
          data.cell.styles.fontStyle = 'bold';
        }
      },
      didDrawPage: (dataArg) => {
        if (dataArg.pageNumber > pageNumber) {
          pageNumber = dataArg.pageNumber;
        }
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.getWidth();
        const pageHeight = pageSize.getHeight();
        doc.setFontSize(8);
        doc.text(`Pagina ${pageNumber}`, pageWidth - marginLeft, pageHeight - 0.3, { align: 'right' });
      }
    });

    const filename = `backorder_${moment().format('DDMMYYYY')}.pdf`;
    
    // Generate PDF as blob instead of using save()
    const pdfBlob = doc.output('blob');
    
    // Use mobile-compatible download method
    downloadPdfWithMobileSupport(pdfBlob, filename);
  } catch (error) {
    console.error('Errore durante la generazione del PDF:', error);
  }
};